import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { RepCell, MetricBar, ScoreCell, LoadingState, ErrorState, EmptyState } from '../components/primitives';
import RangeControl, { DEFAULT_RANGE, rangeLabel } from '../components/RangeControl';
import RepSeriesPanel from '../components/RepSeriesPanel';
import { fmtInt, initialsOf } from '../lib/format';
import { RangeRep, TimeRange } from '../types/superadmin';
import { PRIMARY_RUBRIC_DIMENSIONS } from '../config/rubricDimensions';

type ColKind = 'int' | 'minutes' | 'bar' | 'score';
type SortKey = keyof RangeRep;

// Sessions, Minutes, overall Call quality, then the five reported rubric
// dimensions. (Live-intelligence dims Obj/FAQ, Landmine, Competitor, Playbook
// and the Training column are commented out — re-add them when those features
// turn on for this client.)
// The other five dimensions are still scored and still charted per rep in the
// expanded row (RepSeriesPanel) — they are just not columns here.
const COLUMNS: Array<{ key: SortKey; label: string; kind: ColKind }> = [
  { key: 'sessions', label: 'Sessions', kind: 'int' },
  { key: 'minutes', label: 'Minutes', kind: 'minutes' },
  { key: 'callQualityScore', label: 'Call quality', kind: 'bar' },
  ...PRIMARY_RUBRIC_DIMENSIONS.map((d) => ({
    key: d.key as SortKey,
    label: d.short,
    kind: 'score' as ColKind
  }))
];

// #, Rep, Client + score columns — for the expanded-row colSpan.
const TOTAL_COLS = 3 + COLUMNS.length;

const repKey = (r: RangeRep) => `${r.tenantId}|${r.userId}`;

export default function Reps() {
  const [range, setRange] = useState<TimeRange>(DEFAULT_RANGE);
  const { data, loading, error } = useApiData(() => api.getReps(range), [JSON.stringify(range)]);
  const [sortKey, setSortKey] = useState<SortKey>('callQualityScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expanded, setExpanded] = useState<string | null>(null);

  const reps = useMemo(() => data?.data.reps ?? [], [data]);

  const sorted = useMemo(() => {
    return [...reps].sort((a, b) => {
      const av = a[sortKey] as number | null;
      const bv = b[sortKey] as number | null;
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls always last
      if (bv == null) return -1;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [reps, sortKey, sortDir]);

  const setSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else {
      setSortKey(k);
      setSortDir('desc');
    }
  };
  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === 'desc' ? '↓' : '↑') : '↕');
  const thCls = (k: SortKey) => 'sortable' + (sortKey === k ? ' sorted' : '');
  const toggle = (k: string) => setExpanded((cur) => (cur === k ? null : k));

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Reps · {rangeLabel(range)}</div>
          <h1 className="title">
            <span className="serif">Discovery call scoring, rep by rep</span>
          </h1>
          <p className="subtitle">
            Every rep's overall <b>call quality score</b> and their scores across the{' '}
            <b>10 Lushin Discovery Call Rubric dimensions</b> for the selected period. Click a rep to
            see their activity by day.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <RangeControl value={range} onChange={setRange} />
        </div>
      </div>

      {loading && <LoadingState />}
      {error && <ErrorState message={error} />}

      {!loading && !error && (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>Rep</th>
                <th>Client</th>
                {COLUMNS.map((c) => (
                  <th key={c.key} className={thCls(c.key)} onClick={() => setSort(c.key)}>
                    {c.label} <span className="sort-arr">{arrow(c.key)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r: RangeRep, i) => {
                const k = repKey(r);
                const isOpen = expanded === k;
                return (
                  <React.Fragment key={k}>
                    <tr className="clickable" onClick={() => toggle(k)}>
                      <td>
                        <span className="mono" style={{ color: 'var(--ink-3)' }}>
                          <span className={'caret' + (isOpen ? ' open' : '')}>▸</span>
                          {String(i + 1).padStart(2, '0')}
                        </span>
                      </td>
                      <td>
                        <RepCell initials={initialsOf(r.name)} name={r.name} role={r.email || undefined} />
                      </td>
                      <td>
                        <Link
                          to={`/clients/${r.tenantId}`}
                          className="mono"
                          style={{ color: 'var(--ink-3)', textDecoration: 'none' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {r.displayName}
                        </Link>
                      </td>
                      {COLUMNS.map((c) => {
                        const value = r[c.key] as number | null;
                        return (
                          <td key={c.key}>
                            {c.kind === 'int' && <span className="metric-num">{fmtInt(value ?? 0)}</span>}
                            {c.kind === 'minutes' && (
                              <span className="metric-num">
                                {fmtInt(value ?? 0)}
                                <span className="u">min</span>
                              </span>
                            )}
                            {c.kind === 'bar' && <MetricBar value={value} />}
                            {c.kind === 'score' && <ScoreCell value={value} />}
                          </td>
                        );
                      })}
                    </tr>
                    {isOpen && (
                      <tr className="rep-detail-row">
                        <td colSpan={TOTAL_COLS}>
                          <RepSeriesPanel rep={r} range={range} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={TOTAL_COLS}>
                    <EmptyState>No rep activity in this range. Try a wider period.</EmptyState>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
